/**
 * The customer's own designs.
 *
 * Everything they have made for this product, so a card can be picked up again
 * rather than remade. §6.4.
 *
 * Two rules shape it. Opening a design leaves the page, so anything unsaved has
 * to be dealt with first - silently discarding half an hour of work because
 * somebody clicked the wrong thumbnail is unforgivable. And deleting asks,
 * once, because there is no undo across a page load.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, getConfig } from '../api';
import { useEditor } from '../state/store';
import { ensureStored } from '../state/persist';
import type { Design } from '../model/types';

export function DesignsPanel({
  onClose,
  onTemplates,
}: {
  onClose: () => void;
  /** Opens the shop's templates for this product. */
  onTemplates?: () => void;
}) {
  const config = getConfig();
  const current = useEditor((s) => s.design);

  const [designs, setDesigns] = useState<Design[] | null>(null);
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState('');
  const [confirming, setConfirming] = useState('');

  const load = useCallback(() => {
    api
      .list(config.product.id)
      .then(setDesigns)
      .catch((error) => {
        setDesigns([]);
        setProblem(error instanceof Error ? error.message : 'Your designs could not be listed.');
      });
  }, [config.product.id]);

  useEffect(load, [load]);

  /**
   * Goes to another design, keeping anything unsaved first.
   *
   * Saving rather than warning: the customer has already shown what they want
   * by clicking, and a dialogue asking "are you sure" about work they can
   * simply keep is a dialogue that exists to protect the programmer.
   */
  const open = async (uuid: string) => {
    if (uuid === current?.uuid) return;

    setBusy(uuid);

    const state = useEditor.getState();

    if (state.dirty && state.design && state.doc) {
      try {
        await api.save(state.design.uuid || (await ensureStored()), state.doc, {});
      } catch {
        setBusy('');
        setProblem('This design could not be saved, so we have not moved away from it.');

        return;
      }
    }

    window.location.href = `${config.root.replace(/\/wp-json\/?$/, '')}/?trimbox_editor=1&product=${
      config.product.id
    }&design=${uuid}`;
  };

  const duplicate = async (uuid: string) => {
    setBusy(uuid);

    try {
      const copy = await api.duplicate(uuid);

      setDesigns((list) => (list ? [copy, ...list] : [copy]));
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'That design could not be copied.');
    } finally {
      setBusy('');
    }
  };

  const remove = async (uuid: string) => {
    setBusy(uuid);

    try {
      await api.remove(uuid);

      setDesigns((list) => (list ? list.filter((design) => design.uuid !== uuid) : list));
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'That design could not be deleted.');
    } finally {
      setBusy('');
      setConfirming('');
    }
  };

  return (
    <aside className="tb-side" aria-label="My designs">
      <div className="tb-side__head">
        <h2 className="tb-side__title">My designs</h2>
        {onTemplates && (
          <button type="button" className="tb-btn tb-btn--quiet" onClick={onTemplates}>
            Templates
          </button>
        )}

        <button type="button" className="tb-btn tb-btn--quiet" onClick={onClose}>
          Close
        </button>
      </div>

      {problem && <p className="tb-side__problem">{problem}</p>}

      {designs === null && <p className="tb-side__empty">Looking…</p>}

      {designs !== null && designs.length === 0 && (
        <p className="tb-side__empty">
          Nothing saved yet.
          <br />
          Anything you save appears here.
        </p>
      )}

      <ul className="tb-designs">
        {(designs ?? []).map((design) => (
          <li
            key={design.uuid}
            className={`tb-designs__row${design.uuid === current?.uuid ? ' tb-designs__row--on' : ''}`}
          >
            <button
              type="button"
              className="tb-designs__open"
              disabled={busy === design.uuid}
              onClick={() => void open(design.uuid)}
            >
              {design.thumb ? (
                <img src={design.thumb} alt="" className="tb-designs__thumb" />
              ) : (
                <span className="tb-designs__thumb tb-designs__thumb--none" aria-hidden="true" />
              )}

              <span className="tb-designs__what">
                <strong>{design.title}</strong>
                <span className="tb-designs__when">
                  {design.uuid === current?.uuid ? 'Open now' : when(design.updated)}
                  {design.status === 'ordered' && ' · ordered'}
                </span>
              </span>
            </button>

            <div className="tb-designs__actions">
              <button
                type="button"
                className="tb-btn tb-btn--quiet"
                disabled={busy === design.uuid}
                onClick={() => void duplicate(design.uuid)}
              >
                Copy
              </button>

              {/*
                An ordered design is a record of what was bought. It cannot be
                deleted, and offering a button that always fails would only
                teach the customer to distrust the others.
              */}
              {design.status !== 'ordered' &&
                (confirming === design.uuid ? (
                  <button
                    type="button"
                    className="tb-btn tb-btn--quiet tb-designs__confirm"
                    disabled={busy === design.uuid}
                    onClick={() => void remove(design.uuid)}
                  >
                    Really delete
                  </button>
                ) : (
                  <button
                    type="button"
                    className="tb-btn tb-btn--quiet"
                    onClick={() => setConfirming(design.uuid)}
                  >
                    Delete
                  </button>
                ))}
            </div>
          </li>
        ))}
      </ul>
    </aside>
  );
}

/**
 * When a design was last touched, in words.
 *
 * A date is precise and useless; "yesterday" is what someone actually needs to
 * tell two drafts apart.
 */
function when(stamp: string): string {
  // The server stores UTC without saying so, which Safari reads as local time.
  const at = new Date(stamp.replace(' ', 'T') + 'Z');

  if (isNaN(at.getTime())) return '';

  const minutes = Math.floor((Date.now() - at.getTime()) / 60000);

  if (minutes < 2) return 'just now';
  if (minutes < 60) return `${minutes} minutes ago`;

  const hours = Math.floor(minutes / 60);

  if (hours < 24) return hours === 1 ? 'an hour ago' : `${hours} hours ago`;

  const days = Math.floor(hours / 24);

  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;

  return at.toLocaleDateString();
}
